/**
 * PasteListInput Component
 *
 * CRITICAL FOR DOUG: Allows pasting large lists of values (500+ SKUs from Excel).
 * Supports bulk paste with automatic parsing, deduplication, and validation.
 *
 * Features:
 * - Auto-parse multiple separators (newlines, commas, tabs, semicolons)
 * - Deduplicate values automatically
 * - Type validation (UUID regex, numeric check)
 * - Real-time feedback on parse results
 * - Performance warnings at 1000+ / 5000+ values
 * - No hard limit on values (backend handles)
 */

import { useState, useEffect, useRef } from 'react'
import { Input, Space, Typography, Alert, Button, Tooltip } from 'antd'
import {
  CheckCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import {
  parseValueList,
  getPerformanceWarningLevel,
  getPerformanceWarningMessage,
} from '../../parseUtils'
import type { PasteListInputProps, ParseResult } from '../../advancedFiltersTypes'

const { TextArea } = Input
const { Text } = Typography

export function PasteListInput({
  value,
  onChange,
  fieldType,
  fieldName,
  placeholder,
}: PasteListInputProps) {
  // Track if user is actively editing (to prevent external sync from overwriting)
  const isUserEditing = useRef(false)
  const lastUserEdit = useRef<number>(0)

  // Initialize rawText from value prop (only on mount or when not actively editing)
  const [rawText, setRawText] = useState(() =>
    value && value.length > 0 ? value.join('\n') : ''
  )
  const [parseResult, setParseResult] = useState<ParseResult | null>(null)

  // Sync from external value changes (e.g., when filter row is reset or loaded from defaults)
  // But DON'T sync if user is actively typing (within last 500ms)
  useEffect(() => {
    const timeSinceLastEdit = Date.now() - lastUserEdit.current
    if (isUserEditing.current || timeSinceLastEdit < 500) {
      return // User is typing, don't overwrite
    }

    // External change - sync rawText from value
    if (value && value.length > 0) {
      setRawText(value.join('\n'))
    } else {
      setRawText('')
    }
  }, [value])

  // Parse pasted text on change
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Mark as user editing to prevent external sync from overwriting
    isUserEditing.current = true
    lastUserEdit.current = Date.now()

    const text = e.target.value
    setRawText(text)

    const result = parseValueList(text, fieldType)
    setParseResult(result)
    onChange(result.values)

    // Reset editing flag after a short delay (allow state to settle)
    setTimeout(() => {
      isUserEditing.current = false
    }, 100)
  }

  // Clear all values
  const handleClear = () => {
    setRawText('')
    setParseResult(null)
    onChange([])
  }

  const warningLevel = parseResult
    ? getPerformanceWarningLevel(parseResult.values.length)
    : 'none'
  const warningMessage = parseResult
    ? getPerformanceWarningMessage(parseResult.values.length)
    : null

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      <Text type="secondary" style={{ fontSize: 12 }}>
        Paste values (one per line, or comma/tab separated):
      </Text>

      <TextArea
        value={rawText}
        onChange={handleTextChange}
        placeholder={
          placeholder ||
          `${fieldName}\n${fieldName}-2\n${fieldName}-3\n...\n\n(or paste from Excel)`
        }
        rows={8}
        style={{ fontFamily: 'monospace', fontSize: 13 }}
      />

      {/* Parse result feedback */}
      {parseResult && (
        <Space
          size="small"
          wrap
          style={{
            fontSize: 12,
            padding: '4px 0',
          }}
        >
          {parseResult.values.length > 0 && (
            <Text style={{ fontSize: 12 }}>
              <CheckCircleOutlined
                style={{ color: '#52c41a', marginRight: 4 }}
              />
              {parseResult.values.length.toLocaleString()} value
              {parseResult.values.length !== 1 ? 's' : ''} parsed
            </Text>
          )}

          {parseResult.duplicatesRemoved > 0 && (
            <Text type="warning" style={{ fontSize: 12 }}>
              {parseResult.duplicatesRemoved.toLocaleString()} duplicate
              {parseResult.duplicatesRemoved !== 1 ? 's' : ''} removed
            </Text>
          )}

          {parseResult.emptyLinesSkipped > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {parseResult.emptyLinesSkipped} empty line
              {parseResult.emptyLinesSkipped !== 1 ? 's' : ''} skipped
            </Text>
          )}

          {parseResult.invalidValues.length > 0 && (
            <Tooltip
              title={
                <div>
                  <div style={{ marginBottom: 4 }}>Invalid values:</div>
                  {parseResult.invalidValues.slice(0, 10).map((val, idx) => (
                    <div key={idx} style={{ fontSize: 11, fontFamily: 'monospace' }}>
                      {val}
                    </div>
                  ))}
                  {parseResult.invalidValues.length > 10 && (
                    <div style={{ fontSize: 11, marginTop: 4 }}>
                      ... and {parseResult.invalidValues.length - 10} more
                    </div>
                  )}
                </div>
              }
            >
              <Text type="danger" style={{ fontSize: 12, cursor: 'help' }}>
                <WarningOutlined style={{ marginRight: 4 }} />
                {parseResult.invalidValues.length} invalid value
                {parseResult.invalidValues.length !== 1 ? 's' : ''}
              </Text>
            </Tooltip>
          )}

          {rawText && (
            <Button type="link" size="small" onClick={handleClear} style={{ padding: 0 }}>
              Clear
            </Button>
          )}
        </Space>
      )}

      {/* Performance warning for large lists */}
      {warningMessage && (
        <Alert
          message={
            <Space size={4}>
              {warningLevel === 'warning' ? (
                <WarningOutlined />
              ) : (
                <InfoCircleOutlined />
              )}
              {warningMessage}
            </Space>
          }
          type={warningLevel === 'warning' ? 'warning' : 'info'}
          showIcon={false}
          closable
          style={{ fontSize: 12 }}
        />
      )}
    </Space>
  )
}
